import React, { useState } from 'react';
import { CheckIcon, SearchIcon, MenuIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
interface Step {
  id: string;
  label: string;
}
interface WizardShellProps {
  steps: Step[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  children: ReactNode;
  canGoBack: boolean;
  canGoNext: boolean;
  isLastStep: boolean;
}
export function WizardShell({
  steps,
  currentStep,
  onStepChange,
  onBack,
  onNext,
  onSubmit,
  children,
  canGoBack,
  canGoNext,
  isLastStep
}: WizardShellProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const filteredSteps = steps.filter(step => step.label.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleStepClick = (index: number) => {
    onStepChange(index);
    setMobileMenuOpen(false);
  };
  return <div className="min-h-screen bg-gray-50 flex" data-id="element-72">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-80 bg-slate-900 border-r border-slate-800 p-6" data-id="element-73">
        <div className="mb-6" data-id="element-74">
          <Logo size="sm" variant="light" className="mb-6" data-id="element-75" />
          <h2 className="text-lg font-bold text-white mb-4" data-id="element-76">
            Application Sections
          </h2>
          <div className="relative" data-id="element-77">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-78" />
            <input type="text" placeholder="Quick jump..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-79" />
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto pr-2 custom-scrollbar" data-id="element-80">
          {filteredSteps.map(step => {
          const index = steps.findIndex(s => s.id === step.id);
          const isActive = index === currentStep;
          return <button key={step.id} onClick={() => handleStepClick(index)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${isActive ? 'bg-blue-600 text-white font-medium shadow-sm' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`} data-id="element-81">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isActive ? 'bg-white text-blue-600' : 'bg-slate-800 text-slate-400 border border-slate-700'}`} data-id="element-82">
                  {index + 1}
                </div>
                <span className="text-sm leading-tight" data-id="element-83">{step.label}</span>
              </button>;
        })}
          {filteredSteps.length === 0 && <div className="text-slate-500 text-sm text-center py-4" data-id="element-84">
              No sections found
            </div>}
        </nav>
      </div>

      {/* Mobile Header & Menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20" data-id="element-85">
        <div className="px-4 py-3 flex items-center justify-between" data-id="element-86">
          <Logo size="sm" data-id="element-87" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 -mr-2 text-gray-600 hover:text-gray-900" data-id="element-88">
            {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-89" /> : <MenuIcon className="w-6 h-6" data-id="element-90" />}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg max-h-[70vh] overflow-y-auto" data-id="element-91">
            <div className="p-4 border-b border-gray-100" data-id="element-92">
              <div className="relative" data-id="element-93">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-94" />
                <input type="text" placeholder="Quick jump..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-95" />
              </div>
            </div>
            <nav className="p-2" data-id="element-96">
              {filteredSteps.map(step => {
            const index = steps.findIndex(s => s.id === step.id);
            const isActive = index === currentStep;
            return <button key={step.id} onClick={() => handleStepClick(index)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-97">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-98">
                      {index + 1}
                    </div>
                    <span className="text-sm" data-id="element-99">{step.label}</span>
                  </button>;
          })}
            </nav>
          </div>}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-14 lg:pt-0 h-screen overflow-hidden" data-id="element-100">
        <div className="flex-1 overflow-y-auto px-4 py-6 lg:px-8 lg:py-10" data-id="element-101">
          <div className="max-w-4xl mx-auto" data-id="element-102">
            <div className="mb-6 flex items-center justify-between" data-id="element-103">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900" data-id="element-104">
                {steps[currentStep].label}
              </h1>
              <div className="hidden lg:block text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-105">
                Section {currentStep + 1} of {steps.length}
              </div>
            </div>
            {children}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="border-t border-gray-200 bg-white px-4 py-4 lg:px-8 flex-shrink-0" data-id="element-106">
          <div className="max-w-4xl mx-auto flex justify-between items-center" data-id="element-107">
            <button type="button" onClick={onBack} disabled={!canGoBack} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-108">
              Previous Section
            </button>
            {isLastStep ? <button type="button" onClick={onSubmit} className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm" data-id="element-109">
                Submit Application
              </button> : <button type="button" onClick={onNext} disabled={!canGoNext} className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-110">
                Next Section
              </button>}
          </div>
        </div>
      </div>
    </div>;
}